import { EditOutlined } from '@ant-design/icons'
import { Button, Card, Col, Flex, Row, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  BoosterMilestoneDetail,
  BoosterMilestoneFormValues,
  BoosterMilestoneLinkedPto,
} from 'Modules'
import { memo, useMemo, useState } from 'react'

import { BOOSTER_CODE_EXPIRY_CONFIG, BOOSTER_REWARD_TYPE_CONFIG } from 'constants/booster-program'

import { HorizontalRow } from '../form-create/horizontal-row'

import { MilestoneEditForm } from './milestone-edit-form'

interface Props {
  milestone: BoosterMilestoneDetail
  canEdit?: boolean
  canRemove?: boolean
  /** Previous milestone's threshold — used by the edit form's E07 rule. */
  previousThreshold?: number
  /** Next milestone's threshold — used by the edit form's E08 rule. */
  nextThreshold?: number
  /** Controlled edit-mode flag — true means render the inline edit form. */
  isEditing?: boolean
  /** Another milestone (or the Add form) is currently being edited — hide Edit button. */
  lockEdit?: boolean
  onStartEdit?: () => void
  onStopEdit?: () => void
  onSave?: (id: string, values: BoosterMilestoneFormValues) => Promise<void> | void
  onRemove?: (id: string) => void
}

const formatNumber = (n?: number) =>
  n === undefined ? '-' : new Intl.NumberFormat('en-US').format(n)

const rewardTypeLabel = (rt: BoosterMilestoneDetail['rewardType']) =>
  BOOSTER_REWARD_TYPE_CONFIG[rt] ?? rt

const codeExpiryLabel = (e: BoosterMilestoneDetail['codeExpiry']) =>
  BOOSTER_CODE_EXPIRY_CONFIG[e] ?? e

const renderRewardValue = (m: BoosterMilestoneDetail) => {
  switch (m.rewardType) {
    case 'DISCOUNT_CODE':
      return (
        <HorizontalRow label="Discount Value">
          <Flex gap={8} align="center">
            <div className="booster-detail__value booster-detail__value--sm">{m.discountValue}</div>
            <span className="booster-program-create__inline-label">%</span>
          </Flex>
        </HorizontalRow>
      )
    case 'APR_BOOSTER_CODE':
      return (
        <HorizontalRow label="Boost APR Value">
          <Flex gap={8} align="center">
            <div className="booster-detail__value booster-detail__value--sm">
              +{m.boostAprValue}
            </div>
            <span className="booster-program-create__inline-label">%</span>
          </Flex>
        </HorizontalRow>
      )
    case 'CASHBACK_TOKEN_CODE':
      return (
        <>
          <HorizontalRow label="Cashback Rate">
            <Flex gap={8} align="center">
              <div className="booster-detail__value booster-detail__value--sm">
                {m.cashbackRate}
              </div>
              <span className="booster-program-create__inline-label">%</span>
            </Flex>
          </HorizontalRow>
          <HorizontalRow label="Token Type">
            <div className="booster-detail__value booster-detail__value--field booster-detail__value--select">
              {m.tokenType}
            </div>
          </HorizontalRow>
          <HorizontalRow label="PTO Sold Out">
            <Tag color={m.ptoSoldOut ? 'red' : 'default'}>{m.ptoSoldOut ? 'ON' : 'OFF'}</Tag>
          </HorizontalRow>
        </>
      )
    case 'PTO_BONUS_CODE':
      return (
        <>
          <HorizontalRow label="Bonus Value">
            <Flex gap={8} align="center" wrap="wrap">
              <span className="booster-program-create__inline-label">Buy</span>
              <div className="booster-detail__value booster-detail__value--sm">{m.bonusBuy}</div>
              <span className="booster-program-create__inline-label">get</span>
              <div className="booster-detail__value booster-detail__value--sm">{m.bonusGet}</div>
              <span className="booster-program-create__inline-label">PTO</span>
            </Flex>
          </HorizontalRow>
          <HorizontalRow label="PTO Sold Out">
            <Tag color={m.ptoSoldOut ? 'red' : 'default'}>{m.ptoSoldOut ? 'ON' : 'OFF'}</Tag>
          </HorizontalRow>
        </>
      )
    default:
      return null
  }
}

const BoosterMilestoneSummaryCardImpl = ({
  milestone,
  canEdit = false,
  canRemove = false,
  previousThreshold,
  nextThreshold,
  isEditing,
  lockEdit = false,
  onStartEdit,
  onStopEdit,
  onSave,
  onRemove,
}: Props) => {
  // Fall back to local state when the parent hasn't lifted edit control.
  const [internalEditing, setInternalEditing] = useState(false)
  const controlled = isEditing !== undefined
  const editing = controlled ? isEditing : internalEditing
  const startEdit = () => (controlled ? onStartEdit?.() : setInternalEditing(true))
  const stopEdit = () => (controlled ? onStopEdit?.() : setInternalEditing(false))

  const showPendingClaimed =
    milestone.rewardType === 'CASHBACK_TOKEN_CODE' || milestone.rewardType === 'PTO_BONUS_CODE'

  const linkedColumns = useMemo<ColumnsType<BoosterMilestoneLinkedPto>>(() => {
    const columns: ColumnsType<BoosterMilestoneLinkedPto> = [
      { title: 'PTO', dataIndex: 'ptoName', key: 'ptoName' },
      {
        title: 'Status',
        dataIndex: 'sellingStatus',
        key: 'sellingStatus',
        render: (value: BoosterMilestoneLinkedPto['sellingStatus']) =>
          value === 'SOLD_OUT' ? <Tag color="red">Sold Out</Tag> : <Tag color="green">On sale</Tag>,
      },
      showPendingClaimed
        ? { title: 'Pending', dataIndex: 'pending', key: 'pending', render: formatNumber }
        : { title: 'Applied', dataIndex: 'applied', key: 'applied', render: formatNumber },
    ]
    if (showPendingClaimed) {
      columns.push({ title: 'Claimed', dataIndex: 'claimed', key: 'claimed', render: formatNumber })
    }
    return columns
  }, [showPendingClaimed])

  if (editing && onSave) {
    return (
      <MilestoneEditForm
        milestone={milestone}
        index={milestone.index}
        getPreviousThreshold={() => previousThreshold}
        getNextThreshold={() => nextThreshold}
        onCancel={stopEdit}
        onSave={async (values) => {
          await onSave(milestone.id, values)
          stopEdit()
        }}
      />
    )
  }

  return (
    <Card className="booster-detail__milestone-card">
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={12}
        className="booster-detail__milestone-header"
      >
        <Flex align="center" gap={8} className="booster-detail__milestone-heading">
          <span className="booster-detail__milestone-label">M{milestone.index + 1} —</span>
          <span>Buy ≥</span>
          <span className="booster-detail__milestone-threshold">{milestone.threshold}</span>
          <span className="booster-detail__milestone-label">PTO (cumulative)</span>
        </Flex>
        <Flex gap={16} align="center">
          {canRemove && !lockEdit && (
            <Button
              type="link"
              onClick={() => onRemove?.(milestone.id)}
              className="booster-program-create__milestone-remove"
            >
              Remove
            </Button>
          )}
          {canEdit && !lockEdit && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={startEdit}
              className="booster-detail__milestone-edit-btn"
            >
              Edit
            </Button>
          )}
        </Flex>
      </Flex>

      <Row gutter={[32, 0]}>
        <Col xs={24} lg={12}>
          <HorizontalRow label="Reward Type">
            <div className="booster-detail__value booster-detail__value--field booster-detail__value--select">
              {rewardTypeLabel(milestone.rewardType)}
            </div>
          </HorizontalRow>
          <HorizontalRow label="Total Supply">
            <Flex gap={24} align="center" wrap="wrap">
              <div className="booster-detail__value booster-detail__value--field">
                {formatNumber(milestone.totalSupply)}
              </div>
              <span className="booster-detail__supply-meta">
                Issued: {formatNumber(milestone.issued)}
              </span>
              <span className="booster-detail__supply-meta">
                Remaining: {formatNumber(milestone.remaining)}
              </span>
            </Flex>
          </HorizontalRow>
          <HorizontalRow label="Code Expiry">
            <div className="booster-detail__value booster-detail__value--field booster-detail__value--select">
              {codeExpiryLabel(milestone.codeExpiry)}
            </div>
          </HorizontalRow>
        </Col>

        <Col xs={24} lg={12}>
          {renderRewardValue(milestone)}
        </Col>
      </Row>

      <div className="booster-detail__linked-section">
        <div className="booster-detail__linked-divider" />
        <div className="booster-detail__linked-heading">LINKED PTO</div>
        <Table<BoosterMilestoneLinkedPto>
          columns={linkedColumns}
          dataSource={milestone.linkedPtos}
          rowKey={(r) => r.ptoId}
          pagination={false}
          size="small"
          className="booster-detail__linked-table"
        />
      </div>
    </Card>
  )
}

export const BoosterMilestoneSummaryCard = memo(BoosterMilestoneSummaryCardImpl)
